import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Loader2 } from 'lucide-react'

/**
 * Button — componente base do design system PPM.PAGO.
 *
 * Variantes:
 *   - default: ação primária (brand-600)
 *   - secondary: ação secundária (gray)
 *   - destructive: ação destrutiva (danger-600)
 *   - outline: bordas, sem preenchimento
 *   - ghost: sem bordas, sem preenchimento
 *   - link: aparência de link
 *
 * Tamanhos:
 *   - sm: compacto (tabelas, ações secundárias)
 *   - default: padrão
 *   - lg: ações proeminentes (CTA)
 *   - icon: ícone quadrado
 *
 * Estados: default, hover, focus, disabled, loading
 * Acessibilidade: WCAG AA, foco visível, aria-busy em loading
 */
const buttonVariants = cva(
  // Base: todos os botões compartilham estas classes
  [
    'inline-flex items-center justify-center gap-2',
    'rounded-md font-medium text-sm',
    'transition-colors duration-[150ms] ease-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    'select-none whitespace-nowrap',
    // Garante target mínimo de toque 44px (WCAG 2.5.5)
    'min-h-[44px] min-w-[44px]',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-brand-600 text-white',
          'hover:bg-brand-700',
          'active:bg-brand-800',
        ],
        secondary: [
          'bg-gray-100 text-gray-900',
          'hover:bg-gray-200',
          'active:bg-gray-300',
          'dark:bg-gray-800 dark:text-gray-50 dark:hover:bg-gray-700',
        ],
        destructive: [
          'bg-danger-600 text-white',
          'hover:bg-danger-700',
          'active:bg-danger-800',
        ],
        outline: [
          'border border-gray-300 bg-transparent text-gray-700',
          'hover:bg-gray-50 hover:text-gray-900',
          'dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800',
        ],
        ghost: [
          'bg-transparent text-gray-700',
          'hover:bg-gray-100 hover:text-gray-900',
          'dark:text-gray-300 dark:hover:bg-gray-800',
        ],
        link: [
          'bg-transparent text-brand-600 underline-offset-4',
          'hover:underline',
          'dark:text-brand-400',
          '!min-h-0 !min-w-0 h-auto p-0',
        ],
      },
      size: {
        sm:      'h-9 min-h-[36px] px-3 text-xs rounded max-lg:h-11 max-lg:min-h-[44px]',
        default: 'h-11 px-4 py-2',
        lg:      'h-12 px-8 text-base',
        icon:    'h-11 w-11 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Renderiza como componente filho (Radix Slot) */
  asChild?: boolean
  /** Mostra spinner e desabilita interação */
  loading?: boolean
  /** Texto acessível do spinner (padrão: "Aguarde...") */
  loadingText?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      loadingText = 'Aguarde...',
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    const isDisabled = disabled || loading

    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={isDisabled}
        aria-busy={loading ? 'true' : undefined}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            <span>{loadingText}</span>
          </>
        ) : (
          children
        )}
      </Comp>
    )
  },
)

Button.displayName = 'Button'

export { Button, buttonVariants }
